import { useEffect, useMemo, useState } from "react"
import { MapPinned, Save } from "lucide-react"
import {
  useLazyGetItemCenterAvailabilityMatrixQuery,
  useGetProcurementCentersQuery,
  useUpdateItemCenterAvailabilityMutation,
} from "shared/redux/endpoints/procurementItems"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { Card, Button, Checkbox, EmptyState, Loading, SearchInput, Select } from "../../ui"
import { HScroll } from "procurement/design/components/HScroll"

// §2.5 Item Center Availability — matrix of Item × Center, toggled on/off.
// The GET endpoint returns a flat list of item×center pairs, paginated the
// ordinary way (page/size). Items and centers (the matrix rows/columns) are
// derived client-side from every pair fetched so far — `rows` accumulates
// across pages instead of being replaced, so scrolling never loses what's
// already loaded. `loadMore` fires once the table is scrolled near its right
// edge and `total` says there's more than what's in `rows` yet.
//
// TODO: the GET response shape (flat items + total) and the save contract
// (PUT /items/{id}/center-availability) are both ASSUMPTIONS — not confirmed
// against a real backend build yet.
const PAGE_SIZE = 500
const SCROLL_LOAD_THRESHOLD = 120

const ItemAvailabilityView = () => {
  const [search, setSearch] = useState("")
  const [centerId, setCenterId] = useState("")
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerOptions = useMemo(
    () =>
      (centersPage?.items || []).map((c) => ({
        value: c.centerId,
        label: c.centerName,
      })),
    [centersPage]
  )

  const [fetchMatrix, { isFetching }] = useLazyGetItemCenterAvailabilityMatrixQuery()
  const [updateAvailability, { isLoading: saving }] = useUpdateItemCenterAvailabilityMutation()

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [initialLoading, setInitialLoading] = useState(true)

  // { [itemId]: { [centerId]: boolean } } — local editable copy, merged in as
  // each page's pairs arrive (never wiped wholesale, so in-progress edits on
  // already-loaded rows survive a load-more).
  const [grid, setGrid] = useState({})
  const [dirty, setDirty] = useState(() => new Set())

  const mergeRows = (newRows) => {
    setGrid((prev) => {
      const next = { ...prev }
      ;(newRows || []).forEach((r) => {
        if (r.item?.id == null || r.center?.id == null) return
        next[r.item.id] = { ...next[r.item.id], [r.center.id]: !!r.isOrderable }
      })
      return next
    })
  }

  // Reload from page 0 whenever search or the single-center filter changes.
  useEffect(() => {
    let alive = true
    setInitialLoading(true)
    setPage(0)
    setRows([])
    setGrid({})
    setDirty(new Set())
    fetchMatrix({ search: search || undefined, centerId: centerId || undefined, page: 0, size: PAGE_SIZE })
      .unwrap()
      .then((res) => {
        if (!alive) return
        setRows(res.rows)
        setTotal(res.total)
        mergeRows(res.rows)
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setInitialLoading(false)
      })
    return () => {
      alive = false
    }
  }, [search, centerId])

  const canLoadMore = rows.length < total

  const loadMore = () => {
    if (!canLoadMore || isFetching) return
    const nextPage = page + 1
    setPage(nextPage)
    fetchMatrix({ search: search || undefined, centerId: centerId || undefined, page: nextPage, size: PAGE_SIZE })
      .unwrap()
      .then((res) => {
        setRows((prev) => [...prev, ...res.rows])
        mergeRows(res.rows)
      })
      .catch(() => {})
  }

  const handleTableScroll = (e) => {
    const el = e.currentTarget
    if (el.scrollWidth - el.scrollLeft - el.clientWidth < SCROLL_LOAD_THRESHOLD) {
      loadMore()
    }
  }

  // With a single center picked, force that one column regardless of what
  // came back in `rows` — a center with zero availability rows yet should
  // still show up as a column, not silently render 0 columns.
  const centers = useMemo(() => {
    if (centerId) {
      const opt = centerOptions.find((o) => String(o.value) === String(centerId))
      return [{ id: centerId, name: opt?.label || `Center ${centerId}` }]
    }
    const seen = new Map()
    rows.forEach((r) => {
      if (r.center?.id != null && !seen.has(r.center.id)) {
        seen.set(r.center.id, r.center.centerName || `Center ${r.center.id}`)
      }
    })
    return Array.from(seen, ([id, name]) => ({ id, name }))
  }, [rows, centerId, centerOptions])

  const items = useMemo(() => {
    const seen = new Map()
    rows.forEach((r) => {
      if (r.item?.id != null && !seen.has(r.item.id)) {
        seen.set(r.item.id, { id: r.item.id, name: r.item.name, itemCode: r.item.itemCode })
      }
    })
    return Array.from(seen.values())
  }, [rows])

  const toggle = (itemId, cId) => {
    setGrid((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [cId]: !prev[itemId]?.[cId] },
    }))
    setDirty((prev) => new Set(prev).add(itemId))
  }

  // Only touches centers loaded so far — centers not yet scrolled into view
  // aren't affected until they're loaded.
  const bulkSet = (itemId, value) => {
    setGrid((prev) => ({
      ...prev,
      [itemId]: centers.reduce((acc, c) => ({ ...acc, [c.id]: value }), { ...prev[itemId] }),
    }))
    setDirty((prev) => new Set(prev).add(itemId))
  }

  const handleSaveRow = async (itemId) => {
    const row = grid[itemId] || {}
    const entries = centers.map((c) => ({ centerId: c.id, isOrderable: !!row[c.id] }))
    try {
      await updateAvailability({ id: itemId, entries }).unwrap()
      setDirty((prev) => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
      showToastSuccess("Center availability updated.")
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save availability for this item. Please try again."))
    }
  }

  return (
    <>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ minWidth: 260, flex: 1 }}>
          <SearchInput
            label="Search item"
            placeholder="Search by code or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <Select
            label="Center"
            placeholder="All centers"
            value={centerId}
            onChange={setCenterId}
            options={centerOptions}
          />
        </div>
      </div>

      <Card flush>
        {initialLoading ? (
          <div style={{ padding: 20 }}>
            <Loading />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MapPinned aria-hidden />}
            title="No items to show"
            text="Try a different search term."
          />
        ) : (
          <HScroll className="pc-table-wrap" onScroll={handleTableScroll}>
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Item</th>
                  {centers.map((c) => (
                    <th key={c.id} className="pc-th-right">
                      {c.name}
                    </th>
                  ))}
                  <th className="pc-th-right">Bulk</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="pc-td-strong">{it.name || it.itemCode}</td>
                    {centers.map((c) => (
                      <td key={c.id} className="pc-td-right">
                        <Checkbox
                          checked={!!grid[it.id]?.[c.id]}
                          onChange={() => toggle(it.id, c.id)}
                        />
                      </td>
                    ))}
                    <td className="pc-td-right">
                      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <Button variant="ghost" size="sm" onClick={() => bulkSet(it.id, true)}>
                          All on
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => bulkSet(it.id, false)}>
                          All off
                        </Button>
                      </div>
                    </td>
                    <td className="pc-td-right">
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<Save aria-hidden />}
                        disabled={!dirty.has(it.id) || saving}
                        onClick={() => handleSaveRow(it.id)}
                      >
                        Save
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {canLoadMore && isFetching && (
              <div style={{ padding: "8px 16px" }}>
                <Loading label="Loading more…" />
              </div>
            )}
          </HScroll>
        )}
      </Card>
    </>
  )
}

export default ItemAvailabilityView
